import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { SurveyRequest, SurveyResponse, SurveysService, QuestionType as SurveyQuestionType } from '@src/app/shared/backend/clients';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { QuestionType as QuestionTypeEnum } from '@src/app/shared/enums/QuestionType.enum';
import { QuestionTypeService } from '@src/app/shared/services/questionType.service';
import { SessionService } from '@src/app/shared/services/session.service';

interface LanguageItem {
  id?: number;
  language: string;
  code: string;
}

@Component({
    selector: 'app-edit-survey',
    templateUrl: './edit-survey.component.html',
    styleUrl: './edit-survey.component.scss',
    host: {
        class: 'app-edit-survey'
    },
    standalone: false
})
export class EditSurveyComponent extends BaseViewComponent implements OnInit {
  surveyId = 0;
  survey: SurveyResponse | undefined;
  languages: LanguageItem[] = [];
  questionTypeOptions = this.questionTypeService.getQuestionTypeOptions();

  surveyForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    questions: this.fb.array([])
  }, {
    validators: [this.requireAtLeastOneQuestion()]
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private translate: TranslateService,
    private session: SessionService,
    private surveysService: SurveysService,
    private questionTypeService: QuestionTypeService
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  async init(): Promise<void> {
    await this.loadLanguages();

    this.route.params.subscribe(async params => {
      this.surveyId = Number(params['surveyId']);

      if (this.surveyId > 0) {
        await this.getSurvey();
        this.initForm();
      }
    });
  }

  get questions(): FormArray {
    return this.surveyForm.get('questions') as FormArray;
  }

  get questionForms() {
    return this.questions.controls;
  }

  get isSurveyNameInvalid() {
    return this.isControlInvalid(this.surveyForm.get('name'));
  }

  get isQuestionRequired() {
    return !!this.surveyForm.errors?.['questionRequired'] && this.surveyForm.touched;
  }

  getQuestionTranslationsGroup(questionIndex: number) {
    return this.getQuestionForm(questionIndex).get('questionTranslations') as FormGroup;
  }

  getAnswerOptions(questionIndex: number) {
    return this.getQuestionForm(questionIndex).get('answerOptions') as FormArray;
  }

  getAnswerOptionForms(questionIndex: number) {
    return this.getAnswerOptions(questionIndex).controls;
  }

  getAnswerTranslationsGroup(questionIndex: number, answerIndex: number) {
    return this.getAnswerOptionForm(questionIndex, answerIndex).get('answerTranslations') as FormGroup;
  }

  isFixedValuesQuestion(questionIndex: number) {
    return this.getQuestionType(questionIndex) === QuestionTypeEnum.FIXED_VALUES;
  }

  getQuestionType(questionIndex: number): QuestionTypeEnum {
    const questionTypeValue = this.getQuestionForm(questionIndex).get('questionType')?.value;

    return this.getSingleDropdownValue(questionTypeValue) as QuestionTypeEnum;
  }

  isQuestionCodeInvalid(questionIndex: number) {
    return this.isControlInvalid(this.getQuestionForm(questionIndex).get('code'));
  }

  isAnswerCodeInvalid(questionIndex: number, answerIndex: number) {
    return this.isControlInvalid(this.getAnswerOptionForm(questionIndex, answerIndex).get('code'));
  }

  isAnswerOptionRequired(questionIndex: number) {
    const questionForm = this.getQuestionForm(questionIndex);

    return !!questionForm.errors?.['answerOptionRequired'] && questionForm.touched;
  }

  shouldShowQuestionTranslations(questionIndex: number) {
    return !!this.getQuestionForm(questionIndex).get('showTranslations')?.value;
  }

  shouldShowAnswerTranslations(questionIndex: number, answerIndex: number) {
    return !!this.getAnswerOptionForm(questionIndex, answerIndex).get('showTranslations')?.value;
  }

  async getSurvey(): Promise<void> {
    this.survey = await firstValueFrom(this.surveysService.apiSurveysIdGet(this.surveyId));
  }

  addQuestion() {
    this.questions.push(this.createQuestionForm());
    this.surveyForm.updateValueAndValidity();
  }

  removeQuestion(questionIndex: number) {
    this.questions.removeAt(questionIndex);
    this.surveyForm.updateValueAndValidity();
  }

  addAnswerOption(questionIndex: number) {
    this.getAnswerOptions(questionIndex).push(this.createAnswerOptionForm());
    this.updateQuestionAndSurveyValidity(questionIndex);
  }

  removeAnswerOption(questionIndex: number, answerIndex: number) {
    this.getAnswerOptions(questionIndex).removeAt(answerIndex);
    this.updateQuestionAndSurveyValidity(questionIndex);
  }

  toggleQuestionTranslations(questionIndex: number) {
    const showTranslationsControl = this.getQuestionForm(questionIndex).get('showTranslations');
    const currentValue = !!showTranslationsControl?.value;

    showTranslationsControl?.setValue(!currentValue);
  }

  toggleAnswerTranslations(questionIndex: number, answerIndex: number) {
    const showTranslationsControl = this.getAnswerOptionForm(questionIndex, answerIndex).get('showTranslations');
    const currentValue = !!showTranslationsControl?.value;

    showTranslationsControl?.setValue(!currentValue);
  }

  async submitForm(): Promise<void> {

    if (this.survey?.isPublished) {
      return;
    }

    this.surveyForm.markAllAsTouched();
    this.surveyForm.updateValueAndValidity();

    if (this.surveyForm.invalid) {
      return;
    }

    await firstValueFrom(this.surveysService.apiSurveysUpdateIdPut(this.surveyId, this.buildPayload()));

    this.router.navigate([`/surveys/${this.surveyId}`]);
  }

  initForm(): void {

    if (this.survey?.isPublished) {
      this.surveyForm.disable();
    }

    this.surveyForm.patchValue({
      name: this.survey?.name ?? ''
    });

    this.clearQuestions();

    this.survey?.questions?.forEach(question => {
      const questionForm = this.createQuestionForm();
      const questionType = this.mapQuestionTypeFromResponse(question.questionType);

      questionForm.patchValue({
        code: question.code ?? '',
        questionType: [questionType],
        allowsMultipleAnswers: !!question.allowsMultipleAnswers,
        showTranslations: !!question.questionTranslations?.length
      });

      const questionTranslationsForm = questionForm.get('questionTranslations') as FormGroup;

      question.questionTranslations?.forEach(translation => {
        questionTranslationsForm.get(translation.isoCode ?? '')?.setValue(translation.translation ?? '');
      });

      const answerOptions = questionForm.get('answerOptions') as FormArray;
      this.clearAnswerOptions(answerOptions);

      const answerOptionsFromResponse = question.answerOptions ?? [];

      if (questionType === QuestionTypeEnum.FIXED_VALUES) {
        answerOptionsFromResponse.forEach(answer => {
          const answerForm = this.createAnswerOptionForm();

          answerForm.patchValue({
            code: answer.code ?? '',
            showTranslations: !!answer.answerTranslations?.length
          });

          const answerTranslationsForm = answerForm.get('answerTranslations') as FormGroup;

          answer.answerTranslations?.forEach(translation => {
            answerTranslationsForm.get(translation.isoCode ?? '')?.setValue(translation.translatedAnswer ?? '');
          });

          answerOptions.push(answerForm);
        });

        this.ensureQuestionHasAnswerOption(answerOptions);
      }

      this.questions.push(questionForm);
    });

    this.addInitialQuestion();

    this.surveyForm.updateValueAndValidity();
  }

  buildPayload(): SurveyRequest {
    const surveyName = String(this.surveyForm.get('name')?.value ?? '');

    const questions = this.questions.controls
      .map(questionControl => {
        const questionForm = questionControl as FormGroup;

        const questionTypeValue = questionForm.get('questionType')?.value;
        const selectedQuestionType = this.getSingleDropdownValue(questionTypeValue) as QuestionTypeEnum;
        const questionCode = String(questionForm.get('code')?.value ?? '');

        const questionTranslationsForm = questionForm.get('questionTranslations') as FormGroup;
        const questionTranslations = this.languages
          .map(language => {
            const translation = String(questionTranslationsForm.get(language.code)?.value ?? '');

            return {
              isoCode: language.code,
              translation
            };
          })
          .filter(questionTranslation => !!questionTranslation.translation);

        const answerOptions = selectedQuestionType === QuestionTypeEnum.FIXED_VALUES ? this.getAnswerOptionsByQuestionForm(questionForm) : [];

        const allowsMultipleAnswers = selectedQuestionType === QuestionTypeEnum.FIXED_VALUES ? !!questionForm.get('allowsMultipleAnswers')?.value : false;

        return {
          code: questionCode,
          questionType: this.mapQuestionTypeToPayload(selectedQuestionType),
          allowsMultipleAnswers,
          questionTranslations,
          answerOptions
        };
      })
      .filter(question => !!question.code);

    return {
      name: surveyName,
      questions
    };
  }

  getAnswerOptionsByQuestionForm(questionForm: FormGroup) {
    const answerOptions = questionForm.get('answerOptions') as FormArray;

    return answerOptions.controls
      .map(answerOptionControl => {
        const answerOptionForm = answerOptionControl as FormGroup;
        const answerCode = String(answerOptionForm.get('code')?.value ?? '');
        const answerTranslationsForm = answerOptionForm.get('answerTranslations') as FormGroup;

        const answerTranslations = this.languages
          .map(language => {
            const translatedAnswer = String(answerTranslationsForm.get(language.code)?.value ?? '');

            return {
              isoCode: language.code,
              translatedAnswer
            };
          })
          .filter(answerTranslation => !!answerTranslation.translatedAnswer);

        return {
          code: answerCode,
          answerTranslations
        };
      })
      .filter(answerOption => !!answerOption.code);
  }

  addInitialQuestion() {
    if (this.questions.length > 0) {
      return;
    }

    this.addQuestion();
  }

  async loadLanguages(): Promise<void> {
    await firstValueFrom(this.translate.get('PORTAL_LANGUAGES'));

    const companyLanguages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = companyLanguages.filter(language => language.isEnabled);

    this.languages = enabledLanguages
      .sort((firstLanguage, secondLanguage) => {
        const firstLanguageId = firstLanguage.id ?? 0;
        const secondLanguageId = secondLanguage.id ?? 0;

        return firstLanguageId - secondLanguageId;
      })
      .map(language => {
        const languageCode = language.code || 'unknown';

        return {
          id: language.id,
          code: languageCode,
          language: `PORTAL_LANGUAGES.${languageCode.toUpperCase()}`
        };
      });
  }

  createQuestionForm() {
    const questionForm = this.fb.group(
      {
        code: ['', [Validators.required, Validators.maxLength(100)]],
        questionType: [[QuestionTypeEnum.FIXED_VALUES], Validators.required],
        allowsMultipleAnswers: [false, Validators.required],
        showTranslations: [false],
        questionTranslations: this.createTranslationsForm(),
        answerOptions: this.fb.array([])
      },
      {
        validators: [this.requireAnswerOptionForFixedValuesQuestion()]
      }
    );

    this.addDefaultAnswerOption(questionForm);
    this.handleQuestionTypeChanges(questionForm);

    return questionForm;
  }

  createAnswerOptionForm() {
    return this.fb.group({
      code: ['', [Validators.required, Validators.maxLength(100)]],
      showTranslations: [false],
      answerTranslations: this.createTranslationsForm()
    });
  }

  createTranslationsForm() {
    const translationsForm = this.fb.group({});

    this.languages.forEach(language => {
      translationsForm.addControl(language.code, this.fb.control(''));
    });

    return translationsForm;
  }

  addDefaultAnswerOption(questionForm: FormGroup) {
    const answerOptions = questionForm.get('answerOptions') as FormArray;

    answerOptions.push(this.createAnswerOptionForm());
  }

  handleQuestionTypeChanges(questionForm: FormGroup) {
    const questionTypeControl = questionForm.get('questionType');

    questionTypeControl?.valueChanges.subscribe(questionTypeValue => {
      const selectedQuestionType = this.getSingleDropdownValue(questionTypeValue) as QuestionTypeEnum;

      this.updateAnswerOptionsForQuestionType(questionForm, selectedQuestionType);

      questionForm.updateValueAndValidity();
      this.surveyForm.updateValueAndValidity();
    });
  }

  updateAnswerOptionsForQuestionType(
    questionForm: FormGroup,
    selectedQuestionType: QuestionTypeEnum
  ) {
    const answerOptions = questionForm.get('answerOptions') as FormArray;

    if (selectedQuestionType === QuestionTypeEnum.FIXED_VALUES) {
      this.ensureQuestionHasAnswerOption(answerOptions);
      return;
    }

    this.clearAnswerOptions(answerOptions);
    this.disableMultipleAnswers(questionForm);
  }

  ensureQuestionHasAnswerOption(answerOptions: FormArray) {
    if (answerOptions.length > 0) {
      return;
    }

    answerOptions.push(this.createAnswerOptionForm());
  }

  clearQuestions() {
    while (this.questions.length > 0) {
      this.questions.removeAt(0);
    }
  }

  clearAnswerOptions(answerOptions: FormArray) {
    while (answerOptions.length > 0) {
      answerOptions.removeAt(0);
    }
  }

  disableMultipleAnswers(questionForm: FormGroup) {
    questionForm.get('allowsMultipleAnswers')?.setValue(false, {
      emitEvent: false
    });
  }

  requireAtLeastOneQuestion(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const questions = control.get('questions') as FormArray | null;

      if (!questions || questions.length === 0) {
        return { questionRequired: true };
      }

      return null;
    };
  }

  requireAnswerOptionForFixedValuesQuestion(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const questionTypeValue = control.get('questionType')?.value;
      const selectedQuestionType = this.getSingleDropdownValue(questionTypeValue);
      const answerOptions = control.get('answerOptions') as FormArray | null;

      if (selectedQuestionType !== QuestionTypeEnum.FIXED_VALUES) {
        return null;
      }

      if (!answerOptions || answerOptions.length === 0) {
        return { answerOptionRequired: true };
      }

      const hasValidAnswerOption = answerOptions.controls.some(answerOptionControl => {
        const answerCode = answerOptionControl.get('code')?.value;
        const trimmedAnswerCode = String(answerCode ?? '').trim();

        return !!trimmedAnswerCode;
      });

      return hasValidAnswerOption ? null : { answerOptionRequired: true };
    };
  }

  mapQuestionTypeFromResponse(questionType: unknown): QuestionTypeEnum {
    switch (questionType) {
      case 'FIXED_VALUES':
        return QuestionTypeEnum.FIXED_VALUES;
      case 'TEXT':
        return QuestionTypeEnum.TEXT;
      case 'NUMBER':
        return QuestionTypeEnum.NUMBER;
      default:
        return QuestionTypeEnum.FIXED_VALUES;
    }
  }

  mapQuestionTypeToPayload(questionType: QuestionTypeEnum): SurveyQuestionType {
    switch (questionType) {
      case QuestionTypeEnum.FIXED_VALUES:
        return 'FIXED_VALUES';
      case QuestionTypeEnum.TEXT:
        return 'TEXT';
      case QuestionTypeEnum.NUMBER:
        return 'NUMBER';
    }
  }

  getQuestionForm(questionIndex: number) {
    return this.questions.at(questionIndex) as FormGroup;
  }

  getAnswerOptionForm(questionIndex: number, answerIndex: number) {
    return this.getAnswerOptions(questionIndex).at(answerIndex) as FormGroup;
  }

  getSingleDropdownValue(value: unknown) {
    if (Array.isArray(value)) {
      return value[0];
    }

    return value;
  }

  isControlInvalid(control: AbstractControl | null) {
    if (!control) {
      return false;
    }

    return control.invalid && control.touched;
  }

  updateQuestionAndSurveyValidity(questionIndex: number) {
    const questionForm = this.getQuestionForm(questionIndex);

    questionForm.updateValueAndValidity();
    this.surveyForm.updateValueAndValidity();
  }
}
